import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowUpRight } from "lucide-react";
import { PageHeader } from "./fordon";
import { HubEditorial, faqSchema } from "@/components/HubEditorial";
import { dieselvarmareFaq, dieselvarmareSections } from "@/data/nationella-guider-3";
import {
  dieselvarmareExtraFaq,
  dieselvarmareExtraSections,
  dieselvarmareTldr,
  GUIDE_AUTHOR,
  GUIDE_UPDATED,
} from "@/data/nationella-guider-3-extra";
import hero from "@/assets/guider/dieselvarmare.jpg";

const URL = "https://www.akhusbilfritid.se/dieselvarmare-husbil";

const sections = [...dieselvarmareSections, ...dieselvarmareExtraSections];
const faq = [...dieselvarmareFaq, ...dieselvarmareExtraFaq];

export const Route = createFileRoute("/dieselvarmare-husbil")({
  // FAQ-schemat byggs i loadern så guidens textdata hamnar i sidans egen
  // chunk i stället för i initial-bundlen.
  codeSplitGroupings: [["loader", "component"]],
  loader: () => ({ faqLd: faqSchema(faq) }),
  head: ({ loaderData }) => ({
    meta: [
      { title: "Dieselvärmare husbil 2026 – förbrukning, pris & vinterdrift" },
      {
        name: "description",
        content:
          "Uppmätt dieselförbrukning vid −5, −15 och −25 °C, installationspriser 2026, rätt effekt för din bil och de fem fel vi ser oftast i verkstaden i Kalix.",
      },
      {
        name: "keywords",
        content:
          "dieselvärmare husbil, webasto husbil, eberspächer husbil, dieselvärmare förbrukning, husbil vintercamping värme",
      },
      { property: "og:type", content: "article" },
      { property: "og:title", content: "Dieselvärmare husbil – förbrukning, pris och vinterdrift" },
      {
        property: "og:description",
        content: "Riktiga mätvärden från Norrbotten: liter per dygn, elbehov, priser och vanliga fel.",
      },
      { property: "og:url", content: URL },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          "@id": `${URL}#article`,
          headline: "Dieselvärmare i husbil – förbrukning, pris och vinterdrift",
          url: URL,
          inLanguage: "sv-SE",
          author: { "@type": "Organization", name: "AK Husbil & Fritid" },
          publisher: { "@type": "Organization", name: "AK Husbil & Fritid" },
        }),
      },
      ...(loaderData ? [{ type: "application/ld+json", children: JSON.stringify(loaderData.faqLd) }] : []),
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Hem", item: "https://www.akhusbilfritid.se/" },
            { "@type": "ListItem", position: 2, name: "Guider", item: "https://www.akhusbilfritid.se/guider" },
            { "@type": "ListItem", position: 3, name: "Dieselvärmare husbil", item: URL },
          ],
        }),
      },
    ],
  }),
  component: Page,
});

const facts = [
  { k: "3,8 l/dygn", v: "uppmätt förbrukning vid −15 °C, 4 kW luftvärmare" },
  { k: "25–50 Ah", v: "elbehov per vinterdygn ur batteribanken" },
  { k: "22–30 tkr", v: "4 kW luftvärmare färdigmonterad 2026" },
  { k: "22 %", v: "lägre förbrukning med nattsänkning och hyttisolering" },
];

function Page() {
  return (
    <>
      <PageHeader
        label="Nationell guide · Vinterdrift"
        title={
          <>
            Dieselvärmare — <span className="font-italic-display text-accent">uppmätt</span>, inte gissat.
          </>
        }
        sub="Liter per dygn vid −5, −15 och −25 °C, elbehovet folk glömmer, priser vi själva fakturerar och de fem fel som fyller vår verkstad varje november."
      />

      <section className="container-edge pt-10 md:pt-14">
        <figure className="overflow-hidden rounded-3xl border border-border">
          <img
            src={hero}
            alt="Husbil med tänd inredning parkerad i snötäckt skog i skymning, dieselvärmaren i drift"
            width={1600}
            height={912}
            className="h-auto w-full object-cover"
            decoding="async"
          />
        </figure>
      </section>

      <section className="container-edge pt-10 md:pt-14">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {facts.map((f) => (
            <div key={f.k} className="rounded-3xl border border-border bg-card p-6">
              <p className="font-display text-3xl leading-none text-accent">{f.k}</p>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{f.v}</p>
            </div>
          ))}
        </div>
      </section>

      <div className="pt-14 md:pt-20">
        <HubEditorial
          eyebrow="Guiden"
          sections={sections}
          faq={faq}
          tldr={dieselvarmareTldr}
          updated={GUIDE_UPDATED}
          author={GUIDE_AUTHOR}
        />
      </div>

      <section className="container-edge pb-20 md:pb-24">
        <div className="rounded-3xl border border-border bg-warm p-7 md:p-10">
          <p className="eyebrow">Läs vidare</p>
          <div className="mt-5 grid gap-3 sm:grid-cols-3">
            <Link
              to="/solceller-husbil"
              className="group flex items-center justify-between gap-3 rounded-2xl border border-border bg-card p-5 transition hover:border-accent"
            >
              <span className="text-sm font-medium">Solceller & litium — så dimensionerar du elen</span>
              <ArrowUpRight size={16} className="shrink-0 text-accent transition group-hover:translate-x-0.5" />
            </Link>
            <Link
              to="/husbilsdack"
              className="group flex items-center justify-between gap-3 rounded-2xl border border-border bg-card p-5 transition hover:border-accent"
            >
              <span className="text-sm font-medium">Husbilsdäck — tryck, ålder och vinterkrav</span>
              <ArrowUpRight size={16} className="shrink-0 text-accent transition group-hover:translate-x-0.5" />
            </Link>
            <Link
              to="/husbilar-till-salu"
              className="group flex items-center justify-between gap-3 rounded-2xl border border-border bg-card p-5 transition hover:border-accent"
            >
              <span className="text-sm font-medium">Vinterklara husbilar i vårt lager</span>
              <ArrowUpRight size={16} className="shrink-0 text-accent transition group-hover:translate-x-0.5" />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
